import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  IconUsers,
  IconMessageDots,
  IconPhoneCall,
  IconMessages,
  IconTrendingUp,
  IconLock,
  IconWorld,
  IconPhone,
  IconUsersGroup,
  IconWifi,
  IconActivity,
  IconCalendar,
  IconFile,
  IconFolder,
  IconRefresh,
} from "@tabler/icons-react";
import { useStatisticUserGet, useStatisticMessageGet } from "hooks";
import { ApiUtils, StorageUtils, HumpUtils } from "utils";
import { ApiConstant, KeyConstant, LangConstant } from "const";
import { useTranslation } from "react-i18next";
import UserListDialog from "./UserListDialog";

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "0 MB";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

const GeneralSummaryCards = () => {
  const { t: getLabel, i18n } = useTranslation();
  const currentLocale = i18n.language === "en" ? "en-US" : "vi-VN";
  const { getStatisticUser } = useStatisticUserGet();
  const { getStatisticMessage } = useStatisticMessageGet();
  const [openOnlineUsersModal, setOpenOnlineUsersModal] = useState(false);
  const [userModalState, setUserModalState] = useState({
    open: false,
    joinStatus: undefined,
    branchStatus: undefined,
    filterState: undefined,
    title: "",
  });

  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    pendingUsers: 0,
    disabledUsers: 0,
    suspendedUsers: 0,
    onlineUsers: 0,
    dauUsers: 0,
    wauUsers: 0,
    mauUsers: 0,
    totalMessages: 0,
    totalFiles: 0,
    totalFileSize: 0,
    totalFileSizeFormatted: "0 B",
    textMessages: 0,
    imageMessages: 0,
    fileMessages: 0,
    audioMessages: 0,
    videoMessages: 0,
    linkMessages: 0,
    totalCalls: 0,
    directCalls: 0,
    directCallAccept: 0,
    directCallMissed: 0,
    groupCalls: 0,
    totalChannels: 0,
    publicChannels: 0,
    privateChannels: 0,
    totalGroups: 0,
    engagementNum: 0,
    onlineRateNum: 0,
    onlineRate: "0%",
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(30);

  const getStatisticUserRef = useRef(getStatisticUser);
  const getStatisticMessageRef = useRef(getStatisticMessage);
  const lastFetchedTimeRef = useRef(Date.now());

  useEffect(() => {
    getStatisticUserRef.current = getStatisticUser;
    getStatisticMessageRef.current = getStatisticMessage;
  });

  const fetchGeneralStats = useCallback(async (isSilent = false) => {
    if (isSilent) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    try {
      const branchId = StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_ID);

      // 1. Fetch User Stats and Message Stats in parallel
      const [userResult, msgResult] = await Promise.allSettled([
        getStatisticUserRef.current({ branch_id: branchId }),
        getStatisticMessageRef.current({ include_unit_id: 0 }),
      ]);

      const userRes = userResult.status === "fulfilled" ? userResult.value : null;
      const msgRes = msgResult.status === "fulfilled" ? msgResult.value : null;

      let activeUsersCount = 0;
      let pendingUsersCount = 0;
      let disabledUsersCount = 0;
      let totalUsersCount = 0;
      let onlineUsersCount = 0;
      let dauCount = 0;
      let wauCount = 0;
      let mauCount = 0;

      let totalMessagesCount = 0;
      let totalFilesCount = 0;
      let totalFileSize = 0;
      let textMessagesCount = 0;
      let imageMessagesCount = 0;
      let fileMessagesCount = 0;
      let audioMessagesCount = 0;
      let videoMessagesCount = 0;
      let linkMessagesCount = 0;

      let totalCallsCount = 0;
      let directCallsCount = 0;
      let callAcceptCount = 0;
      let callMissedCount = 0;
      let groupCallsCount = 0;

      if (userRes && userRes.data) {
        activeUsersCount = userRes.data.count_active || 0;
        pendingUsersCount = userRes.data.count_pending || 0;
        disabledUsersCount = userRes.data.count_disabled || 0;
        const suspendedUsersCount = userRes.data.count_no_active_devices || 0;
        // Tổng 4 trạng thái "Chức năng hoạt động"
        totalUsersCount = activeUsersCount + pendingUsersCount + disabledUsersCount + suspendedUsersCount;
        onlineUsersCount = userRes.data.count_online || 0;
        dauCount = userRes.data.count_dau !== undefined ? userRes.data.count_dau : activeUsersCount;
        wauCount = userRes.data.count_wau !== undefined ? userRes.data.count_wau : activeUsersCount;
        mauCount = userRes.data.count_mau !== undefined ? userRes.data.count_mau : activeUsersCount;

        totalMessagesCount = userRes.data.count_total_messages || 0;
        totalFilesCount = userRes.data.count_total_files || 0;
        totalFileSize = userRes.data.count_total_file_size || 0;
        textMessagesCount = userRes.data.count_text_messages || 0;
        imageMessagesCount = userRes.data.count_image_messages || 0;
        fileMessagesCount = userRes.data.count_file_messages || 0;
        audioMessagesCount = userRes.data.count_audio_messages || 0;
        videoMessagesCount = userRes.data.count_video_messages || 0;
        linkMessagesCount = userRes.data.count_link_messages || 0;

        totalCallsCount = userRes.data.count_total_calls || 0;
        directCallsCount = userRes.data.count_direct_calls || 0;
        callAcceptCount = userRes.data.count_direct_call_accept || 0;
        callMissedCount = userRes.data.count_direct_call_missed || 0;
        groupCallsCount = userRes.data.count_group_calls || 0;
      }

      // 2. Populate/supplement from getStatisticMessage if needed
      if (msgRes && Array.isArray(msgRes.data)) {
        const fallbackAccept = msgRes.data.reduce((sum, item) => sum + (item.call_accept || 0), 0);
        const fallbackMissed = msgRes.data.reduce((sum, item) => sum + (item.call_missed || 0) + (item.call_reject || 0), 0);
        const fallbackMsg = msgRes.data.reduce((sum, item) => sum + (item.message_send || 0), 0);

        if (callAcceptCount === 0 && callMissedCount === 0 && (fallbackAccept > 0 || fallbackMissed > 0)) {
          callAcceptCount = fallbackAccept;
          callMissedCount = fallbackMissed;
          directCallsCount = callAcceptCount + callMissedCount;
          totalCallsCount = directCallsCount + groupCallsCount;
        }
        if (totalMessagesCount === 0 && fallbackMsg > 0) {
          totalMessagesCount = fallbackMsg;
          textMessagesCount = fallbackMsg;
        }
      }

      // 3. Fetch Group & Channel counts in parallel
      let totalChannelsCount = 0;
      let publicChannelsCount = 0;
      let privateChannelsCount = 0;
      let totalGroupsCount = 0;

      try {
        const channelParams = HumpUtils.decamelizeKeys({
          branchId,
          paging: 0,
          limit: 10000,
          offset: 0,
          asc: 1,
          groupType: 2,
          filter: JSON.stringify({ group_type: 2 }),
        });
        const groupParams = HumpUtils.decamelizeKeys({
          branchId,
          paging: 0,
          limit: 10000,
          offset: 0,
          asc: 1,
          groupType: 1,
          filter: JSON.stringify({ group_type: 1 }),
        });

        const [channelSettled, groupSettled] = await Promise.allSettled([
          ApiUtils.createApiWithToken(false).get(ApiConstant.GET_CHANNEL_LIST, channelParams),
          ApiUtils.createApiWithToken(false).get(ApiConstant.GET_CHANNEL_LIST, groupParams),
        ]);

        if (channelSettled.status === "fulfilled") {
          const rawChannels = channelSettled.value.data?.data?.data || channelSettled.value.data?.data || [];
          totalChannelsCount = rawChannels.length;
          publicChannelsCount = rawChannels.filter(
            item => item.is_public === 1 || item.isPublic === 1 || item.type === 1 || item.is_public === true || item.private_f === 0 || item.privateF === 0,
          ).length;
          privateChannelsCount = Math.max(0, totalChannelsCount - publicChannelsCount);
        }

        if (groupSettled.status === "fulfilled") {
          const rawGroups = groupSettled.value.data?.data?.data || groupSettled.value.data?.data || [];
          totalGroupsCount = rawGroups.length;
        }
      } catch (err) {
        console.error("Error fetching channels/groups detail:", err);
      }

      const rateNum = totalUsersCount > 0 ? (activeUsersCount / totalUsersCount) * 100 : 0;
      const onlineRateNum = totalUsersCount > 0 ? (onlineUsersCount / totalUsersCount) * 100 : 0;

      setStats({
        totalUsers: totalUsersCount,
        activeUsers: activeUsersCount,
        pendingUsers: pendingUsersCount,
        disabledUsers: disabledUsersCount,
        suspendedUsers: userRes?.data?.count_no_active_devices || 0,
        onlineUsers: onlineUsersCount,
        dauUsers: dauCount,
        wauUsers: wauCount,
        mauUsers: mauCount,
        totalMessages: totalMessagesCount,
        totalFiles: totalFilesCount,
        totalFileSize: totalFileSize,
        totalFileSizeFormatted: formatBytes(totalFileSize),
        textMessages: textMessagesCount,
        imageMessages: imageMessagesCount,
        fileMessages: fileMessagesCount,
        audioMessages: audioMessagesCount,
        videoMessages: videoMessagesCount,
        linkMessages: linkMessagesCount,
        totalCalls: totalCallsCount,
        directCalls: directCallsCount,
        directCallAccept: callAcceptCount,
        directCallMissed: callMissedCount,
        groupCalls: groupCallsCount,
        totalChannels: totalChannelsCount,
        publicChannels: publicChannelsCount,
        privateChannels: privateChannelsCount,
        totalGroups: totalGroupsCount,
        engagementNum: rateNum,
        onlineRateNum: onlineRateNum,
        onlineRate: onlineRateNum.toFixed(1) + "%",
      });
      setLastUpdated(new Date());
      lastFetchedTimeRef.current = Date.now();
    } catch (err) {
      console.error("Error fetching general summary stats:", err);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchGeneralStats(false);
  }, [fetchGeneralStats]);

  // Periodic polling when window/tab is active
  useEffect(() => {
    if (!autoRefreshInterval || autoRefreshInterval <= 0) return;

    const intervalId = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchGeneralStats(true);
      }
    }, autoRefreshInterval * 1000);

    return () => clearInterval(intervalId);
  }, [autoRefreshInterval, fetchGeneralStats]);

  // Refetch when returning to tab/window if stale (> 15s)
  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === "visible") {
        const now = Date.now();
        if (now - lastFetchedTimeRef.current >= 15000) {
          fetchGeneralStats(true);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityOrFocus);
    window.addEventListener("focus", handleVisibilityOrFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus);
      window.removeEventListener("focus", handleVisibilityOrFocus);
    };
  }, [fetchGeneralStats]);

  const totalChannelsAndGroups = stats.totalChannels + stats.totalGroups;

  return (
    <div className="mb-4">
      <style>{`
        @keyframes generalSummarySpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .general-summary-spin {
          animation: generalSummarySpin 1s linear infinite;
        }
      `}</style>

      {/* Realtime & Refresh Toolbar */}
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center gap-2">
          <span className="badge bg-green-lt d-flex align-items-center gap-1 py-1 px-2" style={{ fontSize: "0.75rem" }}>
            <span className="status-dot status-dot-animated bg-green"></span>
            Realtime
          </span>
          {lastUpdated && (
            <span className="text-muted small" style={{ fontSize: "0.78rem" }}>
              {getLabel(LangConstant.TXT_CARD_LAST_UPDATED)}{" "}
              <strong className="text-dark">
                {lastUpdated.toLocaleTimeString(currentLocale, { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
              </strong>
            </span>
          )}
        </div>

        <div className="d-flex align-items-center gap-2">
          <div className="d-flex align-items-center text-muted small" style={{ fontSize: "0.78rem" }}>
            <span className="me-1">{getLabel(LangConstant.TXT_CARD_AUTO_REFRESH)}:</span>
            <select
              className="form-select form-select-sm py-1 px-2"
              style={{ width: "auto", fontSize: "0.78rem" }}
              value={autoRefreshInterval}
              onChange={(e) => setAutoRefreshInterval(Number(e.target.value))}
            >
              <option value={15}>15s</option>
              <option value={30}>30s</option>
              <option value={60}>60s</option>
              <option value={0}>{getLabel(LangConstant.TXT_CARD_REFRESH_OFF)}</option>
            </select>
          </div>

          <button
            type="button"
            className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
            onClick={() => fetchGeneralStats(true)}
            disabled={isRefreshing || isLoading}
            title={getLabel(LangConstant.TXT_CARD_REFRESH)}
          >
            <IconRefresh
              size={15}
              className={isRefreshing ? "general-summary-spin" : ""}
            />
            <span>{getLabel(LangConstant.TXT_CARD_REFRESH)}</span>
          </button>
        </div>
      </div>
      {/* Row 1: 4 Executive Summary Cards */}
      <div className="row row-deck row-cards mb-3">
        {/* Card 1: TOTAL USERS */}
        <div className="col-sm-6 col-lg-3">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <span className="avatar bg-blue-lt text-blue rounded me-2">
                  <IconUsers size={24} />
                </span>
                <div className="text-uppercase text-muted fw-bold" style={{ fontSize: "0.72rem", letterSpacing: "0.04em" }}>
                  {getLabel(LangConstant.TXT_CARD_TOTAL_USERS)}
                </div>
              </div>
              <div className="d-flex align-items-baseline mb-2">
                <div className="h1 mb-0 me-2 fw-bold" style={{ fontSize: "2rem" }}>
                  {isLoading ? "..." : stats.totalUsers.toLocaleString(currentLocale)}
                </div>
                <span className="badge bg-blue-lt">{getLabel(LangConstant.TXT_CARD_ALL_ACCOUNTS)}</span>
              </div>
              <div className="d-flex align-items-center gap-1 text-muted small mb-2 flex-wrap" style={{ fontSize: "0.74rem" }}>
                {/* Vô hiệu hóa — đỏ */}
                <span
                  className="badge bg-red-lt text-danger"
                  style={{ cursor: "pointer" }}
                  onClick={() => setUserModalState({
                    open: true,
                    branchStatus: 0,
                    filterState: undefined,
                    joinStatus: undefined,
                    title: getLabel(LangConstant.TXT_CARD_DISABLED),
                  })}
                  title={getLabel(LangConstant.TXT_CARD_DISABLED)}
                >
                  {stats.disabledUsers.toLocaleString(currentLocale)} {getLabel(LangConstant.TXT_CARD_DISABLED)}
                </span>
                {/* Đã đăng nhập — xanh lá */}
                <span
                  className="badge bg-green-lt text-green"
                  style={{ cursor: "pointer" }}
                  onClick={() => setUserModalState({
                    open: true,
                    branchStatus: 1,
                    filterState: 1,
                    joinStatus: undefined,
                    title: getLabel(LangConstant.TXT_CARD_JOINED),
                  })}
                  title={getLabel(LangConstant.TXT_CARD_JOINED)}
                >
                  {stats.activeUsers.toLocaleString(currentLocale)} {getLabel(LangConstant.TXT_CARD_JOINED)}
                </span>
                {/* Chưa đăng nhập — vàng */}
                <span
                  className="badge bg-warning-lt text-warning"
                  style={{ cursor: "pointer" }}
                  onClick={() => setUserModalState({
                    open: true,
                    branchStatus: 1,
                    filterState: 3,
                    joinStatus: undefined,
                    title: getLabel(LangConstant.TXT_CARD_PENDING),
                  })}
                  title={getLabel(LangConstant.TXT_CARD_PENDING)}
                >
                  {stats.pendingUsers.toLocaleString(currentLocale)} {getLabel(LangConstant.TXT_CARD_PENDING)}
                </span>
                {/* Ngưng hoạt động — xám */}
                <span
                  className="badge bg-secondary-lt text-secondary"
                  style={{ cursor: "pointer" }}
                  onClick={() => setUserModalState({
                    open: true,
                    branchStatus: 1,
                    filterState: 5,
                    joinStatus: undefined,
                    title: getLabel(LangConstant.TXT_CARD_SUSPENDED),
                  })}
                  title={getLabel(LangConstant.TXT_CARD_SUSPENDED)}
                >
                  {stats.suspendedUsers.toLocaleString(currentLocale)} {getLabel(LangConstant.TXT_CARD_SUSPENDED)}
                </span>
              </div>
              <div className="progress progress-sm mb-1">
                {(() => {
                  const total = stats.totalUsers || 1;
                  const w = (v) => `${Math.min((v / total) * 100, 100).toFixed(1)}%`;
                  return (
                    <>
                      <div className="progress-bar bg-danger" style={{ width: w(stats.disabledUsers) }} title={getLabel(LangConstant.TXT_CARD_DISABLED)} />
                      <div className="progress-bar bg-green" style={{ width: w(stats.activeUsers) }} title={getLabel(LangConstant.TXT_CARD_JOINED)} />
                      <div className="progress-bar bg-warning" style={{ width: w(stats.pendingUsers) }} title={getLabel(LangConstant.TXT_CARD_PENDING)} />
                      <div className="progress-bar bg-secondary" style={{ width: w(stats.suspendedUsers) }} title={getLabel(LangConstant.TXT_CARD_SUSPENDED)} />
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: ONLINE USERS */}
        <div className="col-sm-6 col-lg-3">
          <div
            className="card card-link card-link-pop"
            style={{ cursor: "pointer" }}
            onClick={() => setOpenOnlineUsersModal(true)}
            title={getLabel(LangConstant.TXT_CARD_ONLINE_USERS)}
          >
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center">
                  <span className="avatar bg-green-lt text-green rounded me-2">
                    <IconWifi size={24} />
                  </span>
                  <div className="text-uppercase text-muted fw-bold" style={{ fontSize: "0.72rem", letterSpacing: "0.04em" }}>
                    {getLabel(LangConstant.TXT_CARD_ONLINE_USERS)}
                  </div>
                </div>
                <span className="badge bg-green-lt small" style={{ fontSize: "0.68rem" }}>{getLabel(LangConstant.TXT_CARD_DETAILS)}</span>
              </div>
              <div className="d-flex align-items-center justify-content-between my-2">
                <div>
                  <div className="h1 mb-1 fw-bold text-green d-flex align-items-center" style={{ fontSize: "2rem" }}>
                    {isLoading ? "..." : stats.onlineUsers.toLocaleString(currentLocale)}
                    <span
                      className="d-inline-block rounded-circle bg-green me-1 ms-2"
                      style={{ width: 8, height: 8, animation: "pulse 2s infinite" }}
                      title={getLabel(LangConstant.TXT_CARD_REALTIME_ONLINE)}
                    ></span>
                  </div>
                  <div className="text-muted small" style={{ fontSize: "0.76rem" }}>
                    {stats.onlineUsers.toLocaleString(currentLocale)} / {stats.totalUsers.toLocaleString(currentLocale)} {getLabel(LangConstant.TXT_CARD_CONNECTED_APP)}
                  </div>
                </div>
                <div className="position-relative d-flex align-items-center justify-content-center" style={{ width: 64, height: 64 }}>
                  <div
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      background: `conic-gradient(#2fb344 0% ${stats.onlineRateNum}%, rgba(200,200,200,0.2) ${stats.onlineRateNum}% 100%)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      style={{
                        width: 46,
                        height: 46,
                        borderRadius: "50%",
                        backgroundColor: "var(--tblr-card-bg, #ffffff)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        fontSize: "0.78rem",
                      }}
                    >
                      {stats.onlineRate}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Card 3: TOTAL MESSAGES (Hộp Thống Kê Tin Nhắn Riêng Biệt) */}
        <div className="col-sm-6 col-lg-3">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <span className="avatar bg-cyan-lt text-cyan rounded me-2">
                  <IconMessageDots size={24} />
                </span>
                <div className="text-uppercase text-muted fw-bold" style={{ fontSize: "0.72rem", letterSpacing: "0.04em" }}>
                  {getLabel(LangConstant.TXT_CARD_TOTAL_MESSAGES)}
                </div>
              </div>
              <div className="d-flex align-items-baseline mb-2">
                <div className="h1 mb-0 me-2 fw-bold text-cyan" style={{ fontSize: "2rem" }}>
                  {isLoading ? "..." : stats.totalMessages.toLocaleString(currentLocale)}
                </div>
                <span className="badge bg-cyan-lt">{getLabel(LangConstant.TXT_CARD_ALL_TIME)}</span>
              </div>
              {/* Phân loại "Tin cá nhân" / "Tin kênh/nhóm" tạm ẩn: backend chưa tính được chính xác
                  (statistic_message chưa có group_type). Key i18n TXT_CARD_DIRECT_MESSAGES /
                  TXT_CARD_GROUP_MESSAGES được giữ lại để bật lại sau. */}
            </div>
          </div>
        </div>

        {/* Card 4: FILES & STORAGE (Hộp Thống Kê Tệp Tin & Lưu Trữ Riêng Biệt) */}
        <div className="col-sm-6 col-lg-3">
          <div className="card">
            <div className="card-body">
              <div className="d-flex align-items-center mb-2">
                <span className="avatar bg-yellow-lt text-yellow rounded me-2">
                  <IconFolder size={24} />
                </span>
                <div className="text-uppercase text-muted fw-bold" style={{ fontSize: "0.72rem", letterSpacing: "0.04em" }}>
                  {getLabel(LangConstant.TXT_CARD_FILES_STORAGE)}
                </div>
              </div>
              <div className="d-flex align-items-baseline mb-2">
                <div className="h1 mb-0 me-2 fw-bold text-yellow" style={{ fontSize: "2rem" }}>
                  {isLoading ? "..." : stats.totalFiles.toLocaleString(currentLocale)}
                </div>
                <span className="badge bg-yellow-lt">{getLabel(LangConstant.TXT_CARD_UPLOADED_FILES)}</span>
              </div>
              <div className="border-top pt-2 mt-1">
                <div className="d-flex align-items-center justify-content-between text-muted" style={{ fontSize: "0.76rem" }}>
                  <span>
                    <IconFile size={13} className="me-1 text-warning" />
                    {getLabel(LangConstant.TXT_CARD_TOTAL_FILES)}
                  </span>
                  <strong className="text-body">{isLoading ? "..." : stats.totalFiles.toLocaleString(currentLocale)}</strong>
                </div>
                <div className="d-flex align-items-center justify-content-between text-muted mt-1" style={{ fontSize: "0.76rem" }}>
                  <span>
                    <IconFolder size={13} className="me-1 text-teal" />
                    {getLabel(LangConstant.TXT_CARD_STORAGE_USED)}
                  </span>
                  <strong className="text-body text-teal">{isLoading ? "..." : stats.totalFileSizeFormatted}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Row 2: 3 In-depth Feature Cards */}
      <div className="row row-deck row-cards">
        {/* Card 5: USER ACTIVITY (DAU/WAU/MAU) */}
        <div className="col-sm-12 col-lg-4">
          <div className="card p-2">
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <span className="avatar avatar-md bg-teal-lt text-teal rounded me-3">
                  <IconActivity size={28} />
                </span>
                <div>
                  <div className="text-uppercase text-muted fw-bold" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                    {getLabel(LangConstant.TXT_CARD_USER_ENGAGEMENT)}
                  </div>
                  <div className="h1 mb-0 fw-bold" style={{ fontSize: "1.8rem" }}>
                    {isLoading ? "..." : stats.dauUsers.toLocaleString(currentLocale)} <span className="text-muted fs-6 font-normal">DAU (24h)</span>
                  </div>
                </div>
              </div>
              <div className="border-top pt-2">
                <div className="d-flex align-items-center justify-content-between text-muted mb-2" style={{ fontSize: "0.85rem" }}>
                  <span>
                    <IconActivity size={16} className="me-1 text-teal" />
                    <strong>{getLabel(LangConstant.TXT_CARD_DAU_DAILY)}</strong> {stats.dauUsers.toLocaleString(currentLocale)}
                  </span>
                  <span className="badge bg-teal-lt">{stats.totalUsers > 0 ? ((stats.dauUsers / stats.totalUsers) * 100).toFixed(1) : 0}%</span>
                </div>
                <div className="d-flex align-items-center justify-content-between text-muted mb-2" style={{ fontSize: "0.85rem" }}>
                  <span>
                    <IconCalendar size={16} className="me-1 text-blue" />
                    <strong>{getLabel(LangConstant.TXT_CARD_WAU_WEEKLY)}</strong> {stats.wauUsers.toLocaleString(currentLocale)}
                  </span>
                  <span className="badge bg-blue-lt">{stats.totalUsers > 0 ? ((stats.wauUsers / stats.totalUsers) * 100).toFixed(1) : 0}%</span>
                </div>
                <div className="d-flex align-items-center justify-content-between text-muted" style={{ fontSize: "0.85rem" }}>
                  <span>
                    <IconTrendingUp size={16} className="me-1 text-purple" />
                    <strong>{getLabel(LangConstant.TXT_CARD_MAU_MONTHLY)}</strong> {stats.mauUsers.toLocaleString(currentLocale)}
                  </span>
                  <span className="badge bg-purple-lt">{stats.totalUsers > 0 ? ((stats.mauUsers / stats.totalUsers) * 100).toFixed(1) : 0}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Card 6: CUỘC GỌI & HỘI NGHỊ (Gọi đơn vs Gọi nhóm) */}
        <div className="col-sm-12 col-lg-4">
          <div className="card p-2">
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <span className="avatar avatar-md bg-purple-lt text-purple rounded me-3">
                  <IconPhoneCall size={28} />
                </span>
                <div>
                  <div className="text-uppercase text-muted fw-bold" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                    {getLabel(LangConstant.TXT_CARD_CALLS_CONFERENCES)}
                  </div>
                  <div className="h1 mb-0 fw-bold" style={{ fontSize: "1.8rem" }}>
                    {isLoading ? "..." : stats.totalCalls.toLocaleString(currentLocale)} <span className="text-muted fs-6 font-normal">{getLabel(LangConstant.TXT_CARD_CALLS_UNIT)}</span>
                  </div>
                </div>
              </div>
              <div className="border-top pt-2">
                <div className="d-flex align-items-center justify-content-between text-muted mb-2 flex-wrap gap-1" style={{ fontSize: "0.85rem" }}>
                  <span>
                    <IconPhone size={16} className="me-1 text-purple" />
                    <strong>{getLabel(LangConstant.TXT_CARD_DIRECT_CALLS)}</strong> {stats.directCalls.toLocaleString(currentLocale)}
                  </span>
                  <span className="d-flex align-items-center gap-1 flex-wrap" style={{ fontSize: "0.72rem" }}>
                    <span className="badge bg-green-lt text-green">
                      {getLabel(LangConstant.TXT_CARD_CALL_SUCCESS)}: {stats.directCallAccept.toLocaleString(currentLocale)}
                    </span>
                    <span className="badge bg-secondary-lt text-secondary">
                      {getLabel(LangConstant.TXT_CARD_CALL_MISSED)}: {stats.directCallMissed.toLocaleString(currentLocale)}
                    </span>
                  </span>
                </div>
                <div className="d-flex align-items-center justify-content-between text-muted" style={{ fontSize: "0.85rem" }}>
                  <span>
                    <IconUsersGroup size={16} className="me-1 text-indigo" />
                    <strong>{getLabel(LangConstant.TXT_CARD_GROUP_CALLS)}</strong> {stats.groupCalls.toLocaleString(currentLocale)}
                  </span>
                  <span className="badge bg-indigo-lt">{getLabel(LangConstant.TXT_CARD_GROUP_SESSIONS)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Card 7: KÊNH & NHÓM TRAO ĐỔI (Phân loại Kênh Public/Private & Nhóm) */}
        <div className="col-sm-12 col-lg-4">
          <div className="card p-2">
            <div className="card-body">
              <div className="d-flex align-items-center mb-3">
                <span className="avatar avatar-md bg-orange-lt text-orange rounded me-3">
                  <IconMessages size={28} />
                </span>
                <div>
                  <div className="text-uppercase text-muted fw-bold" style={{ fontSize: "0.75rem", letterSpacing: "0.05em" }}>
                    {getLabel(LangConstant.TXT_CARD_CHANNELS_GROUPS)}
                  </div>
                  <div className="h1 mb-0 fw-bold" style={{ fontSize: "1.8rem" }}>
                    {isLoading ? "..." : totalChannelsAndGroups.toLocaleString(currentLocale)} <span className="text-muted fs-6 font-normal">{getLabel(LangConstant.TXT_CARD_CHANNELS_GROUPS_UNIT)}</span>
                  </div>
                </div>
              </div>
              <div className="border-top pt-2">
                <div className="d-flex align-items-center justify-content-between text-muted mb-2 flex-wrap gap-1" style={{ fontSize: "0.85rem" }}>
                  <span>
                    <strong>{getLabel(LangConstant.TXT_CARD_CHANNELS_COUNT)}</strong>{" "}
                    <span className="text-body fw-bold">{stats.totalChannels.toLocaleString(currentLocale)}</span>
                  </span>
                  <span className="d-flex gap-1 align-items-center" style={{ fontSize: "0.72rem" }}>
                    <span className="badge bg-blue-lt">
                      <IconWorld size={12} className="me-1" /> {getLabel(LangConstant.TXT_CARD_PUBLIC)}: {stats.publicChannels.toLocaleString(currentLocale)}
                    </span>
                    <span className="badge bg-secondary-lt">
                      <IconLock size={12} className="me-1" /> {getLabel(LangConstant.TXT_CARD_PRIVATE)}: {stats.privateChannels.toLocaleString(currentLocale)}
                    </span>
                  </span>
                </div>
                <div className="d-flex align-items-center justify-content-between text-muted" style={{ fontSize: "0.85rem" }}>
                  <span>
                    <strong>{getLabel(LangConstant.TXT_CARD_GROUPS_COUNT)}</strong>{" "}
                    <span className="text-body fw-bold">{stats.totalGroups.toLocaleString(currentLocale)}</span>
                  </span>
                  <span className="badge bg-orange-lt" style={{ fontSize: "0.72rem" }}>
                    {getLabel(LangConstant.TXT_CARD_DISCUSSION_GROUPS)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Online Users Modal */}
      {openOnlineUsersModal && (
        <UserListDialog
          open={openOnlineUsersModal}
          onClose={() => setOpenOnlineUsersModal(false)}
          title={getLabel(LangConstant.TXT_CARD_ONLINE_USERS)}
          isOnlineFilter={true}
        />
      )}

      {/* Filtered Status Modal — 4 trạng thái "Chức năng hoạt động" */}
      {userModalState.open && (
        <UserListDialog
          open={userModalState.open}
          onClose={() => setUserModalState({ ...userModalState, open: false })}
          title={userModalState.title}
          branchStatus={userModalState.branchStatus}
          filterState={userModalState.filterState}
          joinStatus={userModalState.joinStatus}
        />
      )}
    </div>
  );
};

export default GeneralSummaryCards;
